
import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Typography, Checkbox, OutlinedInput, ListItemText } from '@mui/material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const BibleLinkSelector = ({ open, handleClose, onSelectLink }) => {
    const { auth } = useContext(AuthContext);
    const [books, setBooks] = useState([]);
    const [selectedBook, setSelectedBook] = useState('');
    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState('');
    const [verses, setVerses] = useState([]);
    const [selectedVerses, setSelectedVerses] = useState([]);

    useEffect(() => {
        const fetchBooks = async () => {
            try {
                const res = await axios.get('/api/bible/books', {
                    headers: {
                        'x-auth-token': auth.token
                    }
                });
                setBooks(res.data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchBooks();
    }, [auth.token]);

    useEffect(() => {
        if (selectedBook) {
            const fetchChapters = async () => {
                try {
                    const res = await axios.get(`/api/bible/books/${selectedBook}/chapters`, {
                        headers: {
                            'x-auth-token': auth.token
                        }
                    });
                    setChapters(res.data);
                    setSelectedChapter('');
                    setSelectedVerses([]);
                } catch (err) {
                    console.error(err);
                }
            };
            fetchChapters();
        }
    }, [selectedBook, auth.token]);

    useEffect(() => {
        if (selectedBook && selectedChapter) {
            const fetchVerses = async () => {
                try {
                    const res = await axios.get(`/api/bible/books/${selectedBook}/${selectedChapter}/verses`, {
                        headers: {
                            'x-auth-token': auth.token
                        }
                    });
                    setVerses(res.data);
                    setSelectedVerses([]); // Clear selected verses when chapter changes
                } catch (err) {
                    console.error(err);
                }
            };
            fetchVerses();
        }
    }, [selectedBook, selectedChapter, auth.token]);

    const handleAddLink = () => {
        if (selectedBook && selectedChapter && selectedVerses.length > 0) {
            const book = books.find(b => b.book_number === selectedBook);
            const newLinks = selectedVerses.map(verse => ({
                book_number: selectedBook,
                book_name: book ? book.long_name : '',
                chapter: selectedChapter,
                verse: verse
            }));
            onSelectLink(newLinks);
            handleClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Выберите стих Библии</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal" variant="standard">
                    <InputLabel>Книга</InputLabel>
                    <Select value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)}>
                        {books.map((book) => (
                            <MenuItem key={book.book_number} value={book.book_number}>
                                {book.long_name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" variant="standard" disabled={!selectedBook}>
                    <InputLabel>Глава</InputLabel>
                    <Select value={selectedChapter} onChange={(e) => setSelectedChapter(e.target.value)}>
                        {chapters.map((chapter) => (
                            <MenuItem key={chapter} value={chapter}>
                                {chapter}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" variant="standard" disabled={!selectedChapter}>
                    <InputLabel id="multiple-verse-label">Стих</InputLabel>
                    <Select
                        labelId="multiple-verse-label"
                        multiple
                        value={selectedVerses}
                        onChange={(e) => setSelectedVerses(e.target.value)}
                        input={<OutlinedInput label="Стих" />}
                        renderValue={(selected) => selected.join(', ')}
                    >
                        {verses.map((verse) => (
                            <MenuItem key={verse.verse} value={verse.verse}>
                                <Checkbox checked={selectedVerses.indexOf(verse.verse) > -1} />
                                <ListItemText primary={`${verse.verse} - ${verse.text.substring(0, 50)}...`} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedBook && selectedChapter && selectedVerses.length > 0 && (
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Выбрано стихов: {selectedVerses.length} ({selectedVerses.join(', ')})
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Отмена</Button>
                <Button onClick={handleAddLink} color="primary" disabled={!selectedBook || !selectedChapter || selectedVerses.length === 0}>
                    Добавить
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BibleLinkSelector;
